/**
 * The Operations screen at /app/ops: what the pipeline did, what it dropped and why, and
 * what is stuck. Pure, like the portfolio: given the view the API returned, render it.
 *
 * It opens with what is wrong rather than with a chart. Every fault this page exists for was
 * a number that already existed and nobody was shown, so a count that means something only
 * against a baseline is never drawn without one, and where there is not enough history the
 * panel says how much it has instead of drawing a line through four points.
 *
 * Nothing here is per person except a mailbox that has stopped producing sources, which is a
 * fault in the plumbing: how long it has been silent, and no volume, no ordering by volume
 * and no total. A throughput figure per mailbox would be a ranking with an operations
 * heading on it.
 */
import type { ComponentChildren } from "preact";
import {
  attemptBucketLabel, cap, fmtDate, fmtDateFull, fmtDay, plural, rollupAge, skipMeaning, stuckBucketLabel, unresolvedMeaning,
  type FunnelLine, type OpsAlert, type OpsView, type SkipLine, type VolumeSignal,
} from "@trellis/core";
import { Tip } from "../components/tip.js";

export type OpsScreenView = OpsView & { scope?: "all" | "programs"; programIds?: string[] };

export interface OpsScreenProps {
  view: OpsScreenView;
  /** The windows the page offers. Undefined leaves the picker out, which is what a test does. */
  windows?: number[];
  onWindow?: (days: number) => void;
}

const WINDOWS = [7, 14, 30];

export function OpsScreen({ view, windows = WINDOWS, onWindow }: OpsScreenProps) {
  const scope = view.scope === "programs"
    ? (view.programIds ?? []).join(", ") || "your contracts"
    : "every contract";
  return (
    <main class="main settings ops">
      <div class="page-head">
        <span class="label">Operations · {scope}</span>
        <h1 class="h-lg">What the pipeline did</h1>
        <p class="lede">
          Sources Trellis observed, what it read, what it dropped and why, and what is still waiting. Counts only:
          nothing on this page is a measure of anybody's work.
        </p>
        <span class="s">
          {/* The window the page actually has, not the one it was asked for. A young record
              counted over six days must not read as a quiet month. */}
          {view.coverage.days < view.coverage.asked
            ? `Counted over the ${plural(view.coverage.days, "day")} the aggregate holds, of ${view.coverage.asked} asked for, to ${fmtDateFull(view.asOf)}.`
            : `Counted over ${plural(view.windowDays, "day")} to ${fmtDateFull(view.asOf)}.`}
          {" · "}
          {view.builtAt ? `aggregate built ${rollupAge(view.builtAt, view.asOf)}` : "the aggregate has never been built"}
        </span>
        {onWindow && (
          <div class="pick-row">
            <span class="seg" role="group" aria-label="Window">
              {windows.map((d) => (
                <button key={d} type="button" class={d === view.windowDays ? "on" : ""} aria-pressed={d === view.windowDays} onClick={() => onWindow(d)}>
                  {d} days
                </button>
              ))}
            </span>
          </div>
        )}
      </div>

      <Alerts alerts={view.alerts} comparable={view.baseline.comparable} needed={view.baseline.needed} />
      <Funnel view={view} />
      <Volume view={view} />
      <Skips view={view} />
      <Unresolved view={view} />
      <Waiting view={view} />
      <Links view={view} />
      <Cadences view={view} />
      <Quiet view={view} />
      <BlindSpots view={view} />
    </main>
  );
}

/** A panel with a heading and an optional caption under its rows. */
function Panel({ title, what, caption, children }: { title: string; what?: string; caption?: string; children: ComponentChildren }) {
  return (
    <section class="panel">
      <h2 class="h-sm">{what ? <Tip what={what}>{title}</Tip> : title}</h2>
      {children}
      {caption && <p class="panel-foot">{caption}</p>}
    </section>
  );
}

/**
 * The top of the page, and its reason for existing. Tone is a word before it is a colour, so
 * "Fault" and "Watch" are written out: a reader who cannot tell red from amber reads the same
 * page as everybody else.
 */
function Alerts({ alerts, comparable, needed }: { alerts: OpsAlert[]; comparable: number; needed: number }) {
  if (alerts.length === 0) {
    return (
      <section class="panel ops-clear">
        <h2 class="h-sm">Nothing is out of line</h2>
        <p class="note">
          No drop against a baseline, no filter taking a bigger share, no backlog with age on it, no link refusing to answer.
          {comparable < needed ? ` History is thin: ${plural(comparable, "comparable day")} against the ${needed} a comparison needs, so a drop today would not be called one yet.` : ""}
        </p>
      </section>
    );
  }
  return (
    <section class="panel">
      <h2 class="h-sm">{plural(alerts.length, "thing")} to look at</h2>
      <ul class="alert-list">
        {alerts.map((a) => (
          <li key={a.id} class={a.tone}>
            <span class={`pill ${a.tone}`}>{a.tone === "bad" ? "Fault" : "Watch"}</span>
            <span class="body">
              <b>{a.title}</b>
              <span class="s">{a.detail}</span>
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

const STAGE_TIPS = {
  ingested: "Sources Trellis wrote a row for. What a poll listed and refused before a row existed is not here; nothing records it.",
  processed: "Read, with no reason recorded against them. These are the ones a skill was given.",
  skipped: "Dropped with a reason. A held first contact counts here as well as under what is waiting: it has been filtered out and may still be re-judged.",
  pending: "No reason, and not read yet. Either in flight or stuck.",
  raised: "Commitments that came out of those sources. A different unit from the four numbers before it: one source can raise several, or none.",
  confirmed: "Of those, the ones somebody accepted. Still open, or closed.",
  unresolved: "Actions a skill read and nobody could be attributed: the author, or the person they asked, is not somebody Trellis can place. Read, not dropped, so no other number here moves when it happens.",
} as const;

/**
 * A share as a percentage. Anything real but under half a point reads as "<1%" rather than
 * "0%": a rule that has just started dropping twenty messages a day out of ninety thousand
 * is flagged as new, and a row that says both "new" and "0%" reads like a defect.
 */
function pct(x: number | undefined): string {
  if (x === undefined) return "—";
  if (x > 0 && x < 0.005) return "<1%";
  return `${Math.round(x * 100)}%`;
}

/** The same rule for a movement in share points, where the sign carries meaning. */
function pts(delta: number | undefined): string {
  if (delta === undefined) return "—";
  const size = Math.abs(delta);
  if (size > 0 && size < 0.005) return `${delta > 0 ? "+" : "−"}<1 pt`;
  return `${delta >= 0 ? "+" : "−"}${Math.abs(Math.round(delta * 100))} pts`;
}

function Funnel({ view }: { view: OpsScreenView }) {
  const t = view.total;
  const cells: { n: number; label: string; what: string }[] = [
    { n: t.ingested, label: "ingested", what: STAGE_TIPS.ingested },
    { n: t.processed, label: "read", what: STAGE_TIPS.processed },
    { n: t.skipped, label: "dropped", what: STAGE_TIPS.skipped },
    { n: t.pending, label: "waiting", what: STAGE_TIPS.pending },
    { n: t.raised, label: "raised", what: STAGE_TIPS.raised },
    { n: t.confirmed, label: "confirmed", what: STAGE_TIPS.confirmed },
    { n: t.unresolved, label: "unattributed", what: STAGE_TIPS.unresolved },
  ];
  return (
    <Panel
      title="The funnel"
      what="Every source observed in the window, and what became of it. The first four numbers count sources and add up to the first; the last three count what came out of them and do not."
      caption={`Sources for the first four, commitments for the next two, actions for the last. ${t.ingested === 0 ? "Nothing was ingested in this window, so no rate is shown rather than a rate of zero." : `${pct(t.readRate)} of what was ingested was read${t.unresolved > 0 ? `, and ${pct(t.unresolvedShare)} of the actions read reached nobody` : ""}.`}`}
    >
      <div class="ops-counts funnel">
        {cells.map((c) => (
          <Tip key={c.label} what={c.what}>
            <span class={`ops-count${c.n === 0 ? " zero" : ""}`}><b class="count-n num">{c.n}</b><span class="count-w">{c.label}</span></span>
          </Tip>
        ))}
      </div>
      {view.funnel.length === 0
        ? <div class="empty">No sources in this window.</div>
        : (
          <div class="ops-scroll">
            <table class="ops-table">
              <thead>
                <tr>
                  <th scope="col">Kind</th>
                  <th scope="col" class="num">Ingested</th>
                  <th scope="col" class="num">Read</th>
                  <th scope="col" class="num">Dropped</th>
                  <th scope="col" class="num">Waiting</th>
                  <th scope="col" class="num">Read rate</th>
                  <th scope="col" class="num">Raised</th>
                  <th scope="col" class="num">Confirmed</th>
                  <th scope="col" class="num">Unattributed</th>
                </tr>
              </thead>
              <tbody>
                {view.funnel.map((f) => <FunnelRow key={f.kind} f={f} />)}
              </tbody>
            </table>
          </div>
        )}
    </Panel>
  );
}

function FunnelRow({ f }: { f: FunnelLine }) {
  return (
    <tr>
      <th scope="row">{f.kind}</th>
      <td class="num">{f.ingested}</td>
      <td class="num">{f.processed}</td>
      <td class="num">{f.skipped}</td>
      <td class="num">{f.pending}</td>
      <td class="num">{pct(f.readRate)}</td>
      <td class="num">{f.raised}</td>
      <td class="num">{f.confirmed}</td>
      {/* The share is beside the count because a count alone cannot say whether it is normal:
          two unattributed out of ninety is a roster gap, two out of two is a broken mapper. */}
      <td class="num">{f.unresolved}{f.unresolved > 0 && <span class="s">{pct(f.unresolvedShare)} of what it found</span>}</td>
    </tr>
  );
}

/**
 * The one chart, and the sentence that makes it mean something. Bars alone say nothing about
 * whether today is bad, which is exactly how a day at a sixth of normal went unnoticed.
 */
function Volume({ view }: { view: OpsScreenView }) {
  const days = view.days;
  const peak = Math.max(1, ...days.map((d) => d.n), view.today.n);
  const overall = view.signals.find((s) => s.kind === "");
  const reading = days.map((d) => `${d.n} on ${fmtDay(d.day)}`).join(", ");
  return (
    <Panel
      title="Sources a day"
      what="One column per day the aggregate has counted. Weekends are marked because mail halves at the weekend, and a Saturday measured against weekdays reads as an outage every week."
      caption={`A day is compared only with days of the same kind, weekday against weekday, from the ${view.baseline.lookbackDays} before it. Today is drawn and never compared: a part day against whole days always looks like a drop.`}
    >
      {days.length === 0
        ? <div class="empty">The aggregate has not counted a day yet.</div>
        : (
          <>
            <div class="ops-days" role="img" aria-label={reading}>
              {days.map((d) => (
                <div key={d.day} class={`col${d.weekend ? " weekend" : ""}${d.day === view.baseline.day ? " subject" : ""}${d.n === 0 ? " nil" : ""}`}>
                  <b class="num">{d.n}</b>
                  <i style={`height:${d.n === 0 ? 0 : Math.max(4, (d.n / peak) * 100)}%`} />
                  <span class="d">{fmtDay(d.day)}</span>
                </div>
              ))}
              <div class="col partial">
                <b class="num">{view.today.n}</b>
                <i style={`height:${view.today.n === 0 ? 0 : Math.max(4, (view.today.n / peak) * 100)}%`} />
                <span class="d">Today</span>
              </div>
            </div>
            <p class="note ops-verdict">{verdictOf(overall, "Everything together")}</p>
            <ul class="plain-list ops-signals">
              {view.signals.filter((s) => s.kind).map((s) => (
                <li key={s.kind}><span class={`dot ${dotOf(s)}`} /><span class="body">{verdictOf(s, cap(s.kind.replace(/-/g, " ")))}</span></li>
              ))}
            </ul>
          </>
        )}
    </Panel>
  );
}

function dotOf(s: VolumeSignal): string {
  return s.state === "down" ? "bad" : s.state === "up" ? "watch" : s.state === "steady" ? "good" : "off";
}

/** The sentence under the chart. It says what it cannot say, rather than implying a verdict. */
export function verdictOf(s: VolumeSignal | undefined, subject: string): string {
  if (!s || s.day === undefined) return `${subject}: no day has been counted in full yet.`;
  const when = fmtDate(s.day);
  if (s.state === "no-baseline") {
    if (s.median === undefined) return `${subject}: ${s.n} on ${when}. ${plural(s.comparable, "comparable day")} of history against the ${s.needed} a comparison needs, so no verdict yet.`;
    return s.median === 0
      ? `${subject}: ${s.n} on ${when}, and nothing on a usual day either. Too little of this kind to measure.`
      : `${subject}: ${s.n} on ${when}. A usual day is ${s.median}, too few for a fall to mean anything, so no verdict.`;
  }
  const against = `against a usual ${s.median} over ${plural(s.comparable, "comparable day")}`;
  if (s.state === "down") return `${subject}: ${s.n} on ${when}, ${against}. Less than half. Something arrived less, or something is refusing more.`;
  if (s.state === "up") return `${subject}: ${s.n} on ${when}, ${against}. More than double, which is usually a backfill rather than a busy day.`;
  return `${subject}: ${s.n} on ${when}, ${against}. Steady.`;
}

/**
 * The panel Faraaz asked for by name. Over-filtering is a reason's share growing, not a
 * count growing, so the share is the column that carries the argument and the count is
 * beside it for scale.
 */
function Skips({ view }: { view: OpsScreenView }) {
  return (
    <Panel
      title="Why a source was dropped"
      what="Every reason written against a source in the window. A rule that has started biting shows up as its share of the day growing while the total holds, which is why the share is here and not only the count."
      caption={!view.baseline.day
        ? "No day has been counted in full yet, so there is no share to compare."
        : view.baseline.comparable < view.baseline.needed
          ? `${plural(view.baseline.comparable, "comparable day")} of history against the ${view.baseline.needed} a comparison needs, so the last three columns are dashes rather than a guess.`
          : `Share on ${fmtDate(view.baseline.day)} against the median share of ${plural(view.baseline.comparable, "comparable day")}. A dash means the reason was not seen on enough of them.`}
    >
      {view.skips.length === 0
        ? <div class="empty">Nothing was dropped in this window.</div>
        : (
          <div class="ops-scroll">
            <table class="ops-table">
              <thead>
                <tr>
                  <th scope="col">Reason</th>
                  <th scope="col" class="num">In window</th>
                  <th scope="col" class="num">Share</th>
                  <th scope="col" class="num">On the day</th>
                  <th scope="col" class="num">Usual</th>
                  <th scope="col" class="num">Move</th>
                </tr>
              </thead>
              <tbody>
                {view.skips.map((s) => <SkipRow key={s.reason} s={s} />)}
              </tbody>
            </table>
          </div>
        )}
    </Panel>
  );
}

function SkipRow({ s }: { s: SkipLine }) {
  const meaning = skipMeaning(s.reason);
  const move = pts(s.delta);
  const tone = s.newly ? "bad" : s.rising ? "warn" : undefined;
  return (
    <tr {...(tone ? { class: tone } : {})}>
      {/* The space before the pill is load-bearing: without it a screen reader reads the
          row header as one word, "answerednew". */}
      <th scope="row">
        <span class="mono">{s.reason}</span>
        {(s.newly || s.rising) && <>{" "}<span class={`pill ${s.newly ? "bad" : "warn"}`}>{s.newly ? "New" : "Rising"}</span></>}
        {meaning && <span class="s">{meaning}</span>}
      </th>
      <td class="num">{s.n}</td>
      <td class="num">{pct(s.share)}</td>
      <td class="num">{pct(s.dayShare)}<span class="s">{s.dayN}</span></td>
      <td class="num">{pct(s.baselineShare)}</td>
      <td class="num">{move}</td>
    </tr>
  );
}

/**
 * The other half of "why nothing arrived". A skip is a source refused before it was read; this
 * is a source read to the end whose actions nobody could be placed against, and the two have
 * different fixes: widen a rule, or map an account onto a person.
 */
function Unresolved({ view }: { view: OpsScreenView }) {
  const t = view.total;
  const found = t.raised + t.unresolved;
  return (
    <Panel
      title="Why an action reached nobody"
      what="An action a skill found in a source it read to the end, which nobody could be attributed to. The source counts as read either way, which is why this is the one number that falls while everything above it holds steady."
      caption={found === 0
        ? "Nothing was extracted in this window, so there is nothing to attribute."
        : `${t.unresolved} of the ${plural(found, "action")} found in the window reached nobody. An unattributed action is on no record and nobody is chasing it.`}
    >
      {view.unresolved.length === 0
        ? <div class="empty">Everything the skills found was attributed to somebody.</div>
        : (
          <ul class="plain-list">
            {view.unresolved.map((u) => (
              <li key={u.reason}>
                <span class="name mono">{u.reason}</span>
                <span class="s">{plural(u.n, "action")}{unresolvedMeaning(u.reason) ? ` · ${unresolvedMeaning(u.reason)}` : ""}</span>
              </li>
            ))}
          </ul>
        )}
    </Panel>
  );
}

function Waiting({ view }: { view: OpsScreenView }) {
  const st = view.stuck;
  return (
    <Panel
      title="Waiting to be read"
      what="Sources with no processed time on them. A source that cannot be read yet is held on purpose, so a configuration gap can be fixed and the message read afterwards; held is not finished."
      caption="Ages run from when Trellis observed the source, not from when it happened. The difference becomes measurable once the ingestion boundary carries its own timestamps."
    >
      {st.total === 0
        ? <div class="empty">Nothing is waiting.</div>
        : (
          <>
            <div class="ops-counts">
              <Tip what="Every source with no processed time, whatever its age.">
                <span class={`ops-count${st.total >= 100 ? " bad" : ""}`}><b class="count-n num">{st.total}</b><span class="count-w">waiting</span></span>
              </Tip>
              <Tip what="How long the oldest one has waited. A backlog with age on it is a backlog nothing is draining.">
                <span class={`ops-count${(st.oldestDays ?? 0) >= 2 ? " bad" : ""}`}><b class="count-n num">{st.oldestDays ?? 0}</b><span class="count-w">{st.oldestDays === 1 ? "day, the oldest" : "days, the oldest"}</span></span>
              </Tip>
            </div>
            <dl class="ops-facts">
              <div>
                <dt>By kind and age</dt>
                <dd>{st.ages.map((a) => `${a.n} ${a.kind}, ${stuckBucketLabel(a.bucket)}`).join(" · ") || "—"}</dd>
              </div>
              <div>
                <dt>Retries already spent</dt>
                <dd>{st.attempts.map((a) => `${a.n} ${attemptBucketLabel(a.bucket)}`).join(" · ") || "—"}</dd>
              </div>
              <div>
                <dt>Held with a reason</dt>
                <dd>{st.held.map((h) => `${h.n} ${h.reason}`).join(" · ") || "None: every one of these is waiting without a reason recorded."}</dd>
              </div>
            </dl>
          </>
        )}
    </Panel>
  );
}

function Links({ view }: { view: OpsScreenView }) {
  return (
    <Panel
      title="Linked systems"
      what="Projects, spaces and channels a contract has pointed at. A link that has not answered is an outage; a link that answers and reads nothing is a configuration, and they are not the same thing."
    >
      {view.links.length === 0
        ? <div class="empty">Every link is answering and reading something.</div>
        : (
          <ul class="plain-list">
            {view.links.map((l) => (
              <li key={`${l.programId}:${l.kind}:${l.key}`}>
                <span class="name">{l.label ?? l.key}</span>{" "}
                <span class={`pill ${l.error ? "bad" : "warn"}`}>{l.error ? "No answer" : "Reads nothing"}</span>
                <span class="s">
                  {cap(l.kind.replace(/-/g, " "))} on {l.programName ?? l.programId}
                  {l.instance ? ` · ${l.instance}` : ""}
                  {l.lastPollHours === undefined ? " · never polled" : ` · polled ${l.lastPollHours === 0 ? "within the hour" : `${plural(l.lastPollHours, "hour")} ago`}`}
                  {typeof l.count === "number" ? ` · ${plural(l.count, "item")} last time` : ""}
                </span>
                <span class="s mono">{l.error ?? l.note}</span>
              </li>
            ))}
          </ul>
        )}
    </Panel>
  );
}

function Cadences({ view }: { view: OpsScreenView }) {
  return (
    <Panel
      title="Cadence periods that closed with nothing"
      what="A missed period is already on the contract's record. It is here because a run of them is usually one broken thing rather than many."
      caption={`Over the last ${plural(view.cadenceWindowDays, "day")}.`}
    >
      {view.cadences.length === 0
        ? <div class="empty">Nothing missed in this window.</div>
        : (
          <ul class="plain-list">
            {view.cadences.map((c) => (
              <li key={`${c.programId}:${c.cadenceId}`}>
                <span class="name">{c.name ?? c.cadenceId}</span>
                <span class="s">{c.name ? `${c.cadenceId} · ` : ""}{c.programName ?? c.programId} · {plural(c.n, "period")} · oldest {fmtDate(c.oldest)}</span>
              </li>
            ))}
          </ul>
        )}
    </Panel>
  );
}

/**
 * The only per-person list, and the wording is the whole point: a mailbox has stopped, which
 * is a fault in the opt-in group, a permission or the poll. There is no count of what it used
 * to produce and no ordering by volume, because that would be a league table.
 */
function Quiet({ view }: { view: OpsScreenView }) {
  if (view.scope === "programs") return null;
  return (
    <Panel
      title="Mailboxes that have stopped"
      what="Opted in, producing sources until recently, and nothing for two days. Usually the opt-in group, a revoked permission, or the poll itself."
      caption="How long it has been silent, and nothing else. How much a mailbox produces is not a measure this page keeps."
    >
      {view.quiet.length === 0
        ? <div class="empty">Every opted-in mailbox has produced something in the last two days.</div>
        : (
          <ul class="plain-list">
            {view.quiet.map((q) => (
              <li key={q.upn}>
                <span class="name">{q.name ?? q.upn}</span>
                <span class="s">{q.name ? `${q.upn} · ` : ""}silent for {plural(Math.round(q.silentHours), "hour")}</span>
              </li>
            ))}
          </ul>
        )}
    </Panel>
  );
}

/** Said out loud, so nobody reads the page above as the whole picture. */
function BlindSpots({ view }: { view: OpsScreenView }) {
  return (
    <Panel title="What this page cannot tell you">
      <ul class="plain-list ops-blind">
        {view.blindSpots.map((b) => <li key={b}><span class="body s">{b}</span></li>)}
      </ul>
    </Panel>
  );
}
